import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useCreateExportTemplate } from "@/hooks/useExportTemplates";
import { FileText, Plus, Save } from "lucide-react";

export function ExportTemplateManager({ workspaceId }: { workspaceId: string }) {
  const createTemplate = useCreateExportTemplate();
  const [showForm, setShowForm] = useState(false);
  const [template, setTemplate] = useState({
    template_name: "",
    target_system: "quickbooks",
    file_format: "csv",
    field_mappings: {},
    is_default: false,
  });

  const handleSave = async () => {
    await createTemplate.mutateAsync({
      ...template,
      workspace_id: workspaceId,
    });
    setShowForm(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Export Template Configuration
        </CardTitle>
        <CardDescription>
          Configure export formats for various accounting systems
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {showForm ? (
          <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
            <div className="space-y-2">
              <Label>Template Name</Label>
              <Input
                value={template.template_name}
                onChange={(e) => setTemplate({ ...template, template_name: e.target.value })}
                placeholder="e.g., QuickBooks Export"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Target System</Label>
                <Select
                  value={template.target_system}
                  onValueChange={(value) => setTemplate({ ...template, target_system: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quickbooks">QuickBooks</SelectItem>
                    <SelectItem value="xero">Xero</SelectItem>
                    <SelectItem value="sage">Sage</SelectItem>
                    <SelectItem value="netsuite">NetSuite</SelectItem>
                    <SelectItem value="generic">Generic</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>File Format</Label>
                <Select
                  value={template.file_format}
                  onValueChange={(value) => setTemplate({ ...template, file_format: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="csv">CSV</SelectItem>
                    <SelectItem value="xlsx">Excel</SelectItem>
                    <SelectItem value="json">JSON</SelectItem>
                    <SelectItem value="xml">XML</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label>Set as Default Template</Label>
              <Switch
                checked={template.is_default}
                onCheckedChange={(checked) => setTemplate({ ...template, is_default: checked })}
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={createTemplate.isPending}>
                <Save className="h-4 w-4 mr-2" />
                Save Template
              </Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Export Template
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
